const MAX_DIMENSION = 2048;
const WEBP_QUALITY = 0.85;
const SKIP_THRESHOLD_BYTES = 100 * 1024; // 100KB

export interface ProcessImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  maxFileSizeBytes?: number;
}

export interface ProcessImageResult {
  file: File;
  wasProcessed: boolean;
  actions: ('resized' | 'compressed')[];
  originalSize: number;
  finalSize: number;
  originalDimensions?: { width: number; height: number };
  finalDimensions?: { width: number; height: number };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

export function calculateDimensions(
  width: number,
  height: number,
  maxWidth: number = MAX_DIMENSION,
  maxHeight: number = MAX_DIMENSION
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

export interface CompressImageOptions {
  quality?: number;
  maxDimension?: number;
}

export async function compressImageToWebP(
  file: File,
  options: CompressImageOptions = {}
): Promise<File> {
  if (file.size < SKIP_THRESHOLD_BYTES && file.type === 'image/webp') {
    return file;
  }

  const quality = options.quality ?? WEBP_QUALITY;
  const maxDim = options.maxDimension ?? MAX_DIMENSION;

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { width, height } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      maxDim,
      maxDim
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/webp',
        quality
      );
    });

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.webp`, { type: 'image/webp' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function processImageForUpload(
  file: File,
  options: ProcessImageOptions = {}
): Promise<ProcessImageResult> {
  const maxWidth = options.maxWidth ?? Infinity;
  const maxHeight = options.maxHeight ?? Infinity;
  const maxFileSizeBytes = options.maxFileSizeBytes;

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const originalDimensions = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    };

    const needsResize =
      img.naturalWidth > maxWidth || img.naturalHeight > maxHeight;
    const needsCompress =
      maxFileSizeBytes !== undefined && file.size > maxFileSizeBytes;

    if (!needsResize && !needsCompress) {
      return {
        file,
        wasProcessed: false,
        actions: [],
        originalSize: file.size,
        finalSize: file.size,
        originalDimensions,
        finalDimensions: { ...originalDimensions },
      };
    }

    const actions: ('resized' | 'compressed')[] = [];
    const targetDimensions = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      maxWidth,
      maxHeight
    );

    if (needsResize) {
      actions.push('resized');
    }
    if (needsCompress) {
      actions.push('compressed');
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetDimensions.width;
    canvas.height = targetDimensions.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    ctx.drawImage(img, 0, 0, targetDimensions.width, targetDimensions.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/webp',
        WEBP_QUALITY
      );
    });

    const baseName = file.name.replace(/\.[^.]+$/, '');
    const processedFile = new File([blob], `${baseName}.webp`, {
      type: 'image/webp',
    });

    return {
      file: processedFile,
      wasProcessed: true,
      actions,
      originalSize: file.size,
      finalSize: processedFile.size,
      originalDimensions,
      finalDimensions: {
        width: targetDimensions.width,
        height: targetDimensions.height,
      },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

import { type MediaType } from './mediaTypeDetection'
export type { MediaType }

export interface MediaCompressionConfig {
  compression_enabled: boolean;
  compression_quality: number;
  compression_max_dimension: number;
}

export interface ProcessMediaResult {
  file: File;
  mediaType: MediaType;
  wasCompressed: boolean;
}

export function detectMediaType(file: File): MediaType | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return null;
}

export async function processMediaForUpload(
  file: File,
  config?: MediaCompressionConfig | null
): Promise<ProcessMediaResult> {
  const mediaType = detectMediaType(file);
  if (!mediaType) {
    throw new Error(`Unsupported file type: ${file.type}`);
  }

  // Video and audio pass through without compression
  if (mediaType !== 'image') {
    return { file, mediaType, wasCompressed: false };
  }

  // Skip compression entirely when explicitly disabled
  if (config && config.compression_enabled === false) {
    return { file, mediaType: 'image', wasCompressed: false };
  }

  // Image compression using category config or defaults
  const options = config?.compression_enabled
    ? { quality: config.compression_quality, maxDimension: config.compression_max_dimension }
    : {};
  const compressed = await compressImageToWebP(file, options);
  return {
    file: compressed,
    mediaType: 'image',
    wasCompressed: compressed !== file,
  };
}

// ── Crop/Resize to Target Dimensions & JPEG Conversion ────────────────

const JPEG_QUALITY = 0.92;

export interface CropAndResizeOptions {
  targetWidth: number;
  targetHeight: number;
  convertToJpeg?: boolean;
}

export interface CropAndResizeResult {
  file: File;
  wasProcessed: boolean;
  actions: ('cropped' | 'resized' | 'jpeg-converted')[];
  finalDimensions: { width: number; height: number };
}

/**
 * Center-crop and resize an image to exact target dimensions.
 * Optionally converts to JPEG (flattens alpha to white background).
 *
 * Algorithm:
 * 1. Calculate target and source aspect ratios
 * 2. Crop the center region matching target aspect ratio
 * 3. Resize to exact target dimensions
 * 4. If convertToJpeg, fill white background first then draw image
 */
export async function cropAndResizeToTarget(
  file: File,
  options: CropAndResizeOptions
): Promise<CropAndResizeResult> {
  const { targetWidth, targetHeight, convertToJpeg: toJpeg } = options;

  // Guard against invalid dimensions that would cause division by zero
  if (targetWidth <= 0 || targetHeight <= 0) {
    return {
      file,
      wasProcessed: false,
      actions: [],
      finalDimensions: { width: 0, height: 0 },
    };
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const sourceWidth = img.naturalWidth;
    const sourceHeight = img.naturalHeight;

    // Early return when source already matches target exactly and no JPEG conversion needed
    if (sourceWidth === targetWidth && sourceHeight === targetHeight && !toJpeg) {
      return {
        file,
        wasProcessed: false,
        actions: [],
        finalDimensions: { width: targetWidth, height: targetHeight },
      };
    }

    const actions: ('cropped' | 'resized' | 'jpeg-converted')[] = [];

    // Determine crop region (center crop to target aspect ratio)
    const targetAR = targetWidth / targetHeight;
    const sourceAR = sourceWidth / sourceHeight;

    let cropWidth: number;
    let cropHeight: number;

    if (Math.abs(sourceAR - targetAR) < 0.001) {
      // Aspect ratios match — no crop needed
      cropWidth = sourceWidth;
      cropHeight = sourceHeight;
    } else if (sourceAR > targetAR) {
      // Source is wider than target — crop width
      cropHeight = sourceHeight;
      cropWidth = Math.round(sourceHeight * targetAR);
      actions.push('cropped');
    } else {
      // Source is taller than target — crop height
      cropWidth = sourceWidth;
      cropHeight = Math.round(sourceWidth / targetAR);
      actions.push('cropped');
    }

    // Center the crop
    const sx = Math.round((sourceWidth - cropWidth) / 2);
    const sy = Math.round((sourceHeight - cropHeight) / 2);

    // Check if resize is needed (crop region differs from target)
    if (cropWidth !== targetWidth || cropHeight !== targetHeight) {
      actions.push('resized');
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Fill white background if converting to JPEG (flatten alpha)
    if (toJpeg) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      actions.push('jpeg-converted');
    }

    // Draw cropped region to canvas at target dimensions
    ctx.drawImage(
      img,
      sx, sy, cropWidth, cropHeight,
      0, 0, targetWidth, targetHeight
    );

    const mimeType = toJpeg ? 'image/jpeg' : 'image/webp';
    const quality = toJpeg ? JPEG_QUALITY : WEBP_QUALITY;
    const ext = toJpeg ? 'jpg' : 'webp';

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        mimeType,
        quality
      );
    });

    const baseName = file.name.replace(/\.[^.]+$/, '');
    const processedFile = new File([blob], `${baseName}.${ext}`, { type: mimeType });

    return {
      file: processedFile,
      wasProcessed: actions.length > 0,
      actions,
      finalDimensions: { width: targetWidth, height: targetHeight },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ── Crop with User-Specified Area ────────────────────────────────────

/** Pixel-coordinate crop region in the source image (from react-easy-crop's croppedAreaPixels). */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Crop an image using a user-specified region, then resize to target dimensions.
 * Used by the crop-adjustment modal after react-easy-crop provides `croppedAreaPixels`.
 *
 * @param file        Source image file
 * @param cropArea    Region to extract (pixels in source image)
 * @param targetWidth  Desired output width
 * @param targetHeight Desired output height
 * @param convertToJpeg  Optionally convert to JPEG (flattens alpha to white)
 */
export async function cropImageWithArea(
  file: File,
  cropArea: CropArea,
  targetWidth: number,
  targetHeight: number,
  convertToJpeg?: boolean
): Promise<CropAndResizeResult> {
  if (targetWidth <= 0 || targetHeight <= 0) {
    return {
      file,
      wasProcessed: false,
      actions: [],
      finalDimensions: { width: 0, height: 0 },
    };
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const actions: ('cropped' | 'resized' | 'jpeg-converted')[] = [];

    // Determine if an actual crop is happening (crop area differs from full image)
    const sourceWidth = img.naturalWidth;
    const sourceHeight = img.naturalHeight;

    // Clamp crop coordinates to image bounds (rounding can push values out of range)
    const clampedX = Math.max(0, Math.min(cropArea.x, sourceWidth));
    const clampedY = Math.max(0, Math.min(cropArea.y, sourceHeight));
    const clampedW = Math.min(cropArea.width, sourceWidth - clampedX);
    const clampedH = Math.min(cropArea.height, sourceHeight - clampedY);

    // Guard against zero-dimension crop region (crop area entirely outside image bounds)
    if (clampedW <= 0 || clampedH <= 0) {
      return { file, wasProcessed: false, actions: [], finalDimensions: { width: targetWidth, height: targetHeight } };
    }

    const isCropped =
      clampedX !== 0 ||
      clampedY !== 0 ||
      clampedW !== sourceWidth ||
      clampedH !== sourceHeight;

    if (isCropped) {
      actions.push('cropped');
    }

    // Check if resize is needed (crop region differs from target dimensions)
    if (clampedW !== targetWidth || clampedH !== targetHeight) {
      actions.push('resized');
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    if (convertToJpeg) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      actions.push('jpeg-converted');
    }

    ctx.drawImage(
      img,
      clampedX, clampedY, clampedW, clampedH,
      0, 0, targetWidth, targetHeight
    );

    const mimeType = convertToJpeg ? 'image/jpeg' : 'image/webp';
    const quality = convertToJpeg ? JPEG_QUALITY : WEBP_QUALITY;
    const ext = convertToJpeg ? 'jpg' : 'webp';

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        mimeType,
        quality
      );
    });

    const baseName = file.name.replace(/\.[^.]+$/, '');
    const processedFile = new File([blob], `${baseName}.${ext}`, { type: mimeType });

    return {
      file: processedFile,
      wasProcessed: actions.length > 0,
      actions,
      finalDimensions: { width: targetWidth, height: targetHeight },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convert an image to JPEG (flatten alpha to white background) without cropping.
 * Preserves original dimensions.
 * If the input is already JPEG, returns it unchanged to avoid re-encoding quality loss.
 */
export async function convertToJpeg(file: File): Promise<CropAndResizeResult> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    // Early return for already-JPEG files — no re-encoding needed
    if (file.type === 'image/jpeg') {
      return {
        file,
        wasProcessed: false,
        actions: [],
        finalDimensions: { width, height },
      };
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Fill white background to flatten alpha channel
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/jpeg',
        JPEG_QUALITY
      );
    });

    const baseName = file.name.replace(/\.[^.]+$/, '');
    const processedFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });

    return {
      file: processedFile,
      wasProcessed: true,
      actions: ['jpeg-converted'],
      finalDimensions: { width, height },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
